import { Group, SaveButton } from '@/components/_common';
import { Alert, Button, Checkbox, Form, Modal, ModalProps } from 'antd';
import StatusTag from '@/components/_common/StatusTag';
import TabsCustom from '@/components/_common/TabsCustom';
import { Key, useState } from 'react';
import ModalLoadingOverlay from '@/components/_common/ModalLoadingOverlay';
import { useInvoiceFormContext } from '../../InvoiceFormProvider';
import InvoicePartIcmsForm from '../PartFormsModal/IcmsForm';
import InvoicePartShareForm from '../PartFormsModal/ShareForm';
import InvoicePartIpiForm from '../PartFormsModal/IpiForm';
import InvoicePartPisForm from '../PartFormsModal/PisForm';
import InvoicePartCofinsForm from '../PartFormsModal/CofinsForm';
import InvoicePartIrpjCsllForm from '../PartFormsModal/IrpjCsllForm';
import InvoicePartFuelForm from '../PartFormsModal/FuelForm';
import { InvoicePartFormValues } from '../PartFormsModal/InvoicePartForm.types';
import {
  InvoiceItemUpdateTaxesRequest,
  UpdateItemTaxes,
  UpdateTaxesFuel,
} from '@/core/domain/invoice/invoiceItem/invoiceItem.types';
import { useUpdateInvoiceItemTaxes } from '@/core/domain/invoice/invoiceItem/invoiceItem.hook';
import IbsCbsFormFields from '@/components/_common/TaxReform/IbsCbsFormFields';

interface Props extends ModalProps {
  selectedPartIds?: Key[];
  onCancel: VoidFunction;
}

const UpdateTaxesModal: React.FC<Props> = ({ selectedPartIds, ...props }) => {
  const { disabledFields, invoiceId } = useInvoiceFormContext();

  const [form] = Form.useForm<InvoicePartFormValues>();

  const [menuKey, setMenuKey] = useState('icms');
  const [submitLoading, setSubmitLoading] = useState(false);
  const [overwriteUnfilledFields, setOverwriteUnfilledFields] = useState(false);

  const { mutateAsync: updateInvoiceItemTaxes, isLoading } =
    useUpdateInvoiceItemTaxes();

  const hasLoading = submitLoading || isLoading;

  function handleClose() {
    form.resetFields();
    setMenuKey('icms');
    props.onCancel();
  }

  async function handleSubmit(closeModal = true) {
    try {
      setSubmitLoading(true);

      const values = form.getFieldsValue();

      const itemTaxes = {
        ...values.itemTaxes.icms,
        ...values.itemTaxes.ipi,
        ...values.itemTaxes.pis,
        ...values.itemTaxes.cofins,
        ...values.itemTaxes.share,
        ...values.itemTaxes.irpjCsll,
        ...values.itemTaxes.ibsCbs,
      };

      const payload: InvoiceItemUpdateTaxesRequest = {
        invoiceId,
        fuel: values.fuel as UpdateTaxesFuel,
        invoiceItemIds: selectedPartIds as number[],
        itemTaxes: itemTaxes as UpdateItemTaxes,
        overwriteUnfilledFields,
      };

      await updateInvoiceItemTaxes(payload);

      if (closeModal) {
        handleClose();
      }
    } finally {
      setSubmitLoading(false);
    }
  }

  const modalTitle = (
    <Group gap={16}>
      <StatusTag label="NFe" />
      <span>Atualizar as tributações das peças da nota fiscal</span>
    </Group>
  );

  const modalFooter = (
    <Group position="right" gap={24}>
      <Checkbox
        checked={overwriteUnfilledFields}
        onChange={({ target }) => {
          setOverwriteUnfilledFields(target.checked);
        }}
      >
        Substituir campos não preenchidos
      </Checkbox>

      <Button onClick={handleClose} disabled={hasLoading}>
        Cancelar
      </Button>

      <SaveButton
        onClick={() => handleSubmit()}
        disabled={hasLoading || disabledFields}
      >
        Atualizar
      </SaveButton>
    </Group>
  );

  return (
    <Modal
      {...props}
      onCancel={handleClose}
      title={modalTitle}
      width={1200}
      bodyStyle={{ padding: 0 }}
      maskClosable={false}
      destroyOnClose
      footer={modalFooter}
      closable={!hasLoading}
    >
      <div style={{ marginBottom: 24, position: 'relative' }}>
        {hasLoading && (
          <ModalLoadingOverlay
            messages={[
              'Calculando dados de tributos...',
              'Atualizando listagem de itens da nota...',
            ]}
          />
        )}

        {!selectedPartIds?.length && (
          <Alert
            type="warning"
            description="Caso nenhum item da listagem seja selecionado, as alterações serão aplicadas a todos os itens."
            style={{ marginBottom: 24 }}
          />
        )}

        <TabsCustom key="general" activeKey={menuKey} onChange={setMenuKey}>
          <TabsCustom.Item key="icms" label="ICMS">
            <InvoicePartIcmsForm
              form={form}
              isUpdateTaxes={!overwriteUnfilledFields}
            />
          </TabsCustom.Item>

          <TabsCustom.Item key="share" label="Partilha">
            <InvoicePartShareForm form={form} />
          </TabsCustom.Item>

          <TabsCustom.Item key="ipi" label="IPI">
            <InvoicePartIpiForm form={form} />
          </TabsCustom.Item>

          <TabsCustom.Item key="pis" label="PIS">
            <InvoicePartPisForm form={form} />
          </TabsCustom.Item>

          <TabsCustom.Item key="cofins" label="COFINS">
            <InvoicePartCofinsForm form={form} />
          </TabsCustom.Item>

          <TabsCustom.Item key="irpjCsll" label="IRRF/CSLL">
            <InvoicePartIrpjCsllForm form={form} />
          </TabsCustom.Item>

          <TabsCustom.Item key="fuel" label="Combustível">
            <InvoicePartFuelForm form={form} />
          </TabsCustom.Item>

          <TabsCustom.Item key="ibsCbs" label="IBS/CBS">
            <IbsCbsFormFields form={form} disabledFields={disabledFields} />
          </TabsCustom.Item>
        </TabsCustom>
      </div>
    </Modal>
  );
};

export default UpdateTaxesModal;
